import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, impersonation } from "@/lib/api";
import { formatFCFA } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Building2, Home, Users, Wallet, TrendingUp, TrendingDown,
  CircleDollarSign, Percent, ShieldAlert, LogIn, Trophy, Crown,
} from "lucide-react";
import Pagination, { paginate } from "@/components/Pagination";

const TONES = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  teal: "bg-teal-50 text-teal-700 border-teal-200",
};

const KPI = ({ icon: Icon, label, value, sub, tone = "teal", testid }) => (
  <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white hover:shadow-md transition-shadow duration-200" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${TONES[tone] || TONES.teal}`}>
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
    </div>
    <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{label}</div>
    <div className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
  </Card>
);

export default function DevDashboard() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dev/dashboard").then((r) => setData(r.data));
  }, []);

  const access = (s) => {
    if (s.status !== "active") return;
    impersonation.start(s.id, s.nom);
    navigate("/");
  };

  if (!data) {
    return (
      <div>
        <PageHeader title="Tableau de bord DEV — Vue globale" subtitle="Chargement…" testid="page-dev-dashboard" accent="amber" />
      </div>
    );
  }

  const t = data.totals;
  const pag = paginate(data.societies, page);

  return (
    <div data-testid="page-dev-dashboard">
      <PageHeader
        title="Tableau de bord DEV"
        subtitle="Vue consolidée multi-société — agrégats temps réel sur l'ensemble du parc."
        testid="page-dev-dashboard-header"
        accent="amber"
      />

      <div className="mb-6 rounded-xl bg-gradient-to-r from-[#1F5F5B] to-[#2c8e87] text-white p-5 flex items-center justify-between gap-4 border border-amber-300/30 shadow-md">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-400/20 border border-amber-300/40 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300">Mode DEV global</div>
            <div className="font-heading text-lg font-bold">{t.societies} société{t.societies > 1 ? "s" : ""} pilotée{t.societies > 1 ? "s" : ""} — {t.societies_active} active{t.societies_active > 1 ? "s" : ""} · {t.societies_closed} fermée{t.societies_closed > 1 ? "s" : ""}</div>
          </div>
        </div>
        <Button onClick={() => navigate("/dev")} className="bg-amber-400 hover:bg-amber-500 text-amber-950 font-semibold" data-testid="btn-go-dev">
          Gérer les sociétés →
        </Button>
      </div>

      {/* Row 1: Portfolio KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
        <KPI icon={Building2} label="Sociétés actives" value={`${t.societies_active}/${t.societies}`} sub={t.societies_closed ? `${t.societies_closed} fermée(s)` : "Aucune fermeture"} tone="teal" testid="kpi-dev-societies" />
        <KPI icon={Home} label="Maisons totales" value={t.maisons} sub={`${t.maisons_occupees} occupées · ${t.maisons - t.maisons_occupees} libres`} tone="indigo" testid="kpi-dev-maisons" />
        <KPI icon={Percent} label="Taux d'occupation" value={`${t.occupancy_rate}%`} sub="Toutes sociétés confondues" tone="violet" testid="kpi-dev-occupation" />
        <KPI icon={Users} label="Locataires actifs" value={t.locataires_actifs} sub={`${t.proprietaires} propriétaires · ${t.users} utilisateurs`} tone="emerald" testid="kpi-dev-locataires" />
      </div>

      {/* Row 2: Money KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <KPI icon={TrendingUp} label={`Revenu cumulé ${data.mois}`} value={formatFCFA(t.revenu_mois)} sub="Tous paiements encaissés" tone="emerald" testid="kpi-dev-revenu" />
        <KPI icon={TrendingDown} label={`Dépenses cumulées ${data.mois}`} value={formatFCFA(t.depenses_mois)} sub="Toutes catégories" tone="rose" testid="kpi-dev-depenses" />
        <KPI icon={CircleDollarSign} label="Bénéfice net du mois" value={formatFCFA(t.benefice_mois)} sub="Revenu − Dépenses" tone={t.benefice_mois >= 0 ? "amber" : "rose"} testid="kpi-dev-benefice" />
      </div>

      {/* Top performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <Card className="p-5 rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="card-top-revenue">
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-4 h-4 text-amber-500" />
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-700">Top 5 sociétés — revenu du mois</div>
          </div>
          {data.top_by_revenue.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Aucun revenu enregistré ce mois.</div>
          ) : (
            <ul className="space-y-2">
              {data.top_by_revenue.map((s, i) => (
                <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-6 h-6 rounded-md text-xs font-bold flex items-center justify-center ${i === 0 ? "bg-amber-400 text-amber-950" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{s.nom}</div>
                      <div className="text-[11px] text-slate-500">{s.maison_count} maisons · {s.locataires_actifs} actifs</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-emerald-700">{formatFCFA(s.revenu_mois)}</div>
                    <div className="text-[10px] text-slate-400">bénéfice {formatFCFA(s.benefice_mois)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5 rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="card-top-occupancy">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-teal-600" />
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-700">Top 5 sociétés — taux d'occupation</div>
          </div>
          {data.top_by_occupancy.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Aucune maison enregistrée pour le moment.</div>
          ) : (
            <ul className="space-y-2">
              {data.top_by_occupancy.map((s, i) => (
                <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-6 h-6 rounded-md text-xs font-bold flex items-center justify-center ${i === 0 ? "bg-teal-500 text-white" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{s.nom}</div>
                      <div className="text-[11px] text-slate-500">{s.maisons_occupees}/{s.maison_count} maisons occupées</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-teal-700">{s.occupancy_rate}%</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Detailed table */}
      <Card className="rounded-xl border border-slate-200 shadow-sm bg-white overflow-hidden" data-testid="card-societies-table">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Détail par société</div>
          <div className="font-heading font-semibold text-slate-900">Performances du mois {data.mois}</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Société</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Maisons</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Locataires</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Revenu</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Dépenses</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Bénéfice</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pag.rows.map((s) => (
              <TableRow key={s.id} className="hover:bg-slate-50/60" data-testid={`row-dev-society-${s.id}`}>
                <TableCell className="font-medium text-slate-900">{s.nom}</TableCell>
                <TableCell>
                  {s.status === "active"
                    ? <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">Active</Badge>
                    : <Badge className="bg-rose-100 text-rose-800 border border-rose-200 hover:bg-rose-100">Fermée</Badge>}
                </TableCell>
                <TableCell className="text-slate-700">
                  <span className="font-medium">{s.maison_count}</span>
                  <span className="text-slate-400">/{s.max_maisons}</span>
                  <span className="text-xs text-slate-500 ml-2">({s.occupancy_rate}%)</span>
                </TableCell>
                <TableCell className="text-slate-700">{s.locataires_actifs}</TableCell>
                <TableCell className="text-right font-semibold text-emerald-700">{formatFCFA(s.revenu_mois)}</TableCell>
                <TableCell className="text-right font-semibold text-rose-700">{formatFCFA(s.depenses_mois)}</TableCell>
                <TableCell className={`text-right font-bold ${s.benefice_mois >= 0 ? "text-amber-700" : "text-rose-700"}`}>{formatFCFA(s.benefice_mois)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    onClick={() => access(s)}
                    disabled={s.status !== "active"}
                    className="h-8 px-2.5 bg-teal-700 hover:bg-teal-800 text-white text-xs font-semibold gap-1 disabled:opacity-50"
                    data-testid={`btn-dev-access-${s.id}`}
                  >
                    <LogIn className="w-3.5 h-3.5" /> Accéder
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination {...pag} onChange={setPage} testid="pagination-dev-societies" />
    </div>
  );
}
